import { GameData, CostProductionFormulas } from "../game-data.js";
import ResourceVector from "../resource-vector.js";
import BuildingCostFormulas from "./building-cost-formulas.js";

export default class ResourceProductionFormulas {

    // What does the building produce per hour
    static getRawBuildingProduction(building, level) {
        if (!building.production) {
            return new ResourceVector().init();
        }

        // Get raw production of the building.
        const result = new ResourceVector().init().initSparse(building.production(level));
        const total_production = result.sumUpVectorSparse();
        if (total_production === 0) {
            return new ResourceVector().init();
        }

        // Production of headquarters does not scale with level.
        if (building.disable_production_scaling) {
            level = 1;
        }

        // Scale with the level and speed.
        const scale_factor = CostProductionFormulas.getScaleFactor(level, 0.8);
        result.scaleVectorSparse(scale_factor / total_production);
        result.scaleVectorSparse(GameData.SPEED).ceil();
        return result;
    }

    static getBuildingProduction(building, level, bonuses) {
        const production = ResourceProductionFormulas.getRawBuildingProduction(building, level);
        if (bonuses != null) {
            bonuses.forEach(bonus => production[bonus.resource] *= bonus.factor);
        }
        return production;
    }

    // What does the building consume per hour
    static getRawBuildingConsumption(building, level) {
        if (!building.consumption) {
            return new ResourceVector();
        }
        return new ResourceVector().initSparse(building.consumption(level)).scaleVectorSparse(level * GameData.SPEED);
    }

    static getRawWorkerProduction(building, level, current_worker_count, tax_percentage) {
        let result = 0;
        if (building.worker_production_const && level > 0) {
            result += building.worker_production_const;
        }

        if (building.worker_replication_factor && level > 0) {
            const tax_rate_multiplier = (100 - tax_percentage) / 100;
            result += building.worker_replication_factor * current_worker_count * tax_rate_multiplier;
        }

        result *= GameData.SPEED;
        return Math.floor(result);
    }

    static getRawMoneyProduction(current_worker_count, tax_percentage) {
        const paying_worker_count = Math.floor(current_worker_count);
        const rate_multiplier = tax_percentage / 100;
        return Math.floor(0.1 * paying_worker_count * rate_multiplier) * GameData.SPEED;
    }

    static getRawBuildingEnergyProduction(building, level) {
        const factor = building.energy_production_factor;
        if (!building.energy_production_factor) {
            return 0;
        }

        if (building.disable_production_scaling) {
            level = 1;
        }

        return Math.floor(CostProductionFormulas.getScaleFactor(level, factor));
    }

    static getRawEnergyStats(planet) {
        // Calculate the energy production.
        const production = GameData.BUILDINGS.reduce((acc, building) => {
            let energy = ResourceProductionFormulas.getRawBuildingEnergyProduction(building, planet.buildings[building.id]);
            return acc + energy;
        }, 0);

        // Calculate the energy consumption.
        const consumption = GameData.BUILDINGS.reduce((acc, building) => {
            let energy = BuildingCostFormulas.getEnergyCost(building, planet.buildings[building.id]);
            return acc + energy;
        }, 0);

        // Calculate the efficiency.
        let efficiency = Math.min(1.0, production / consumption);
        if (production === 0) {
            efficiency = 0.0;
        }
        if (consumption === 0) {
            efficiency = 1.0;
        }
        return { production: production, consumption: consumption, efficiency: efficiency };
    }

    static getRawStorageCapacity(storage_level) {
        const production_per_building = CostProductionFormulas.getScaleFactor(storage_level, 0.8);
        const hours = Math.pow(storage_level, 0.8) * 2 + 12;
        return 1000 + Math.floor(hours * production_per_building * GameData.SPEED);
    }

    static getRawHousingCapacity(housing_level) {
        const scale_factor = CostProductionFormulas.getScaleFactor(housing_level, 20);
        return Math.floor(scale_factor + 500);
    }

    static getPlanetWorkerProduction(planet, tax_percentage) {
        let total_production = 0;
        const current_worker_count = planet.resources.worker + planet.working_worker_count;
        for (let i = 0; i < GameData.WORKER_PRODUCTION_BUILDINGS.length; i++) {
            let building = GameData.WORKER_PRODUCTION_BUILDINGS[i];
            let building_level = planet.buildings[building.id];
            total_production += ResourceProductionFormulas.getRawWorkerProduction(building, building_level, current_worker_count, tax_percentage);
        }

        return total_production;
    }

    // Calculates the hourly production and consumption of a planet
    // It assumes that the current efficiency of each building is set
    // Hence the server-side resource calculations should have already run
    // Hence this is only used on the client
    // NOTE: we can not merge this with the production on the server side, because the server needs to know the consumption/production of individual buildings to be able to calculate the efficiencies
    static getPlanetResourceDelta(planet) {
        // Energy stats
        const energy = ResourceProductionFormulas.getRawEnergyStats(planet);

        // Normal resource production and consumption
        const total_consumption = new ResourceVector().init();
        const total_production = new ResourceVector().init()
        for (let i = 0; i < GameData.PRODUCTION_BUILDINGS.length; i++) {
            let building = GameData.PRODUCTION_BUILDINGS[i];
            let building_level = planet.buildings[building.id];
            let efficiency = planet.building_efficiencies[building.id] * energy.efficiency;
            let production = ResourceProductionFormulas.getBuildingProduction(building, building_level, planet.astro_info.bonuses);
            let consumption = ResourceProductionFormulas.getRawBuildingConsumption(building, building_level);
            total_production.addVectorSparse(total_production, production.scaleVectorSparse(efficiency));
            total_consumption.addVectorSparse(total_consumption, consumption.scaleVectorSparse(efficiency));
        }

        // Worker production
        total_production.worker = ResourceProductionFormulas.getPlanetWorkerProduction(planet, planet.tax_percentage);

        return [total_consumption, total_production];
    }
}
